import { apiUrl as configApiUrl } from '../config';
import { getAuthHeaders } from '../api/authHeaders';
import logger from '../services/Logger';

// Cache configuration
const CACHE_PREFIX = 'photon_metadata_';
const CACHE_TTL = process.env.REACT_APP_CACHE_TTL_DAYS
  ? parseInt(process.env.REACT_APP_CACHE_TTL_DAYS) * 24 * 60 * 60 * 1000
  : 365 * 24 * 60 * 60 * 1000; // Default 365 days - URL metadata is historical reference
const CACHE_ENABLED = process.env.REACT_APP_DISABLE_CACHE !== 'true';

// Cache utility functions
const getCacheKey = (url, includeRedirectInfo = false) => {
  return `${CACHE_PREFIX}${btoa(url)}_${includeRedirectInfo}`;
};

const getBatchCacheKey = (urls, includeRedirectInfo = false) => {
  const sortedUrls = [...urls].sort(); // Ensure consistent ordering
  return `${CACHE_PREFIX}batch_${btoa(sortedUrls.join('|'))}_${includeRedirectInfo}`;
};

const getCachedData = (cacheKey) => {
  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < CACHE_TTL) {
        return data;
      } else {
        // Remove expired cache
        localStorage.removeItem(cacheKey);
      }
    }
  } catch (error) {
    logger.warn('urlMetadata', 'Cache read error', { error: error.message });
  }
  return null;
};

const setCachedData = (cacheKey, data) => {
  try {
    const cacheEntry = {
      data,
      timestamp: Date.now(),
      version: '1.0' // For future cache invalidation if needed
    };
    localStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
  } catch (error) {
    logger.warn('urlMetadata', 'Cache write error', { error: error.message });
    // If localStorage is full, try to clear old metadata cache entries
    clearOldCache();
  }
};

const clearOldCache = () => {
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch (error) {
    logger.warn('urlMetadata', 'Cache cleanup error', { error: error.message });
  }
};

// Utility function to fetch URL metadata from backend API
export const fetchURLMetadata = async (url, includeRedirectInfo = false) => {
  // Define cache key for use throughout function
  const cacheKey = getCacheKey(url, includeRedirectInfo);

  // Check cache first (if enabled)
  if (CACHE_ENABLED) {
    const cachedData = getCachedData(cacheKey);

    if (cachedData) {
      return cachedData;
    }
  }

  try {
    // Use config for API URL, fallback to relative path for local dev
    const apiBaseUrl = configApiUrl ?
      configApiUrl.replace(/\/$/, '') : // Remove trailing slash
      '';
    const apiUrl = `${apiBaseUrl}/api/url/metadata`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        url,
        include_redirect_info: includeRedirectInfo
      }),
    });

    if (!response.ok) {
      throw new Error(`Backend API returned ${response.status}`);
    }

    const metadata = await response.json();

    const result = {
      title: metadata.title || extractDomain(url),
      description: metadata.description || `Visit ${extractDomain(url)}`,
      domain: metadata.domain || extractDomain(url),
      favicon: metadata.favicon || `https://www.google.com/s2/favicons?domain=${extractDomain(url)}&sz=32`,
      thumbnail: metadata.thumbnail,
      author: metadata.author,
      video_id: metadata.video_id,
      duration: metadata.duration,
      view_count: metadata.view_count,
      redirect_info: metadata.redirect_info,
      realUrl: metadata.url || metadata.resolved_url || url, // Use resolved URL or fallback to original
      url: metadata.url || metadata.resolved_url || url
    };

    // Cache the successful result (if enabled)
    if (CACHE_ENABLED) {
      setCachedData(cacheKey, result);
    }

    return result;

  } catch (error) {
    logger.warn('urlMetadata', 'Backend URL metadata failed, using fallback', { error: error.message });

    // Fallback: extract basic info from URL and use Google favicon service
    const domain = extractDomain(url);
    return {
      title: domain,
      description: `Visit ${domain}`,
      favicon: `https://www.google.com/s2/favicons?domain=${domain}&sz=32`,
      domain: domain
    };
  }
};

// Batch URL metadata fetching for multiple URLs
export const fetchBatchURLMetadata = async (urls, includeRedirectInfo = false) => {
  // Check cache first (if enabled)
  if (CACHE_ENABLED) {
    const batchCacheKey = getBatchCacheKey(urls, includeRedirectInfo);
    const cachedBatch = getCachedData(batchCacheKey);

    if (cachedBatch) {
      return cachedBatch;
    }
  }

  // Check individual caches (if enabled)
  const cachedResults = [];
  const uncachedUrls = [];
  const uncachedIndicesByUrl = new Map();

  const trackUncachedUrl = (url, index) => {
    if (!uncachedIndicesByUrl.has(url)) {
      uncachedIndicesByUrl.set(url, []);
      uncachedUrls.push(url);
    }
    uncachedIndicesByUrl.get(url).push(index);
  };

  if (CACHE_ENABLED) {
    for (let i = 0; i < urls.length; i++) {
      const url = urls[i];
      const individualCacheKey = getCacheKey(url, includeRedirectInfo);
      const cachedData = getCachedData(individualCacheKey);

      if (cachedData) {
        cachedResults[i] = cachedData;
      } else {
        cachedResults[i] = null;
        trackUncachedUrl(url, i);
      }
    }

    // If all URLs are cached, return cached results
    if (uncachedUrls.length === 0) {
      return cachedResults;
    }
  } else {
    // If caching is disabled, fetch all URLs
    for (let i = 0; i < urls.length; i++) {
      cachedResults[i] = null;
      trackUncachedUrl(urls[i], i);
    }
  }

  try {
    const apiBaseUrl = configApiUrl ?
      configApiUrl.replace(/\/$/, '') :
      '';
    const apiUrl = `${apiBaseUrl}/api/url/batch-metadata`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        urls: uncachedUrls,
        include_redirect_info: includeRedirectInfo
      }),
      // Increase timeout for Vertex AI redirects
      signal: AbortSignal.timeout(120000), // 2 minutes timeout
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Backend API returned ${response.status}: ${errorText}`);
    }

    const metadataArray = await response.json();

    // Merge cached and fetched results
    const finalResults = [...cachedResults];

    metadataArray.forEach((metadata, index) => {
      const url = uncachedUrls[index];
      const originalIndices = uncachedIndicesByUrl.get(url) || [];

      // Try to extract the resolved URL from redirect_info if available
      let resolvedUrl = metadata.url || metadata.resolved_url || metadata.final_url;

      // If redirect_info exists and contains URL information, try to extract it
      if (!resolvedUrl && metadata.redirect_info) {
        if (typeof metadata.redirect_info === 'string') {
          // If redirect_info is a URL string
          if (metadata.redirect_info.startsWith('http')) {
            resolvedUrl = metadata.redirect_info;
          }
        } else if (metadata.redirect_info && typeof metadata.redirect_info === 'object') {
          // If redirect_info is an object, try common field names
          resolvedUrl = metadata.redirect_info.final_url ||
                       metadata.redirect_info.resolved_url ||
                       metadata.redirect_info.destination_url ||
                       metadata.redirect_info.url;
        }
      }

      const result = {
        title: metadata.title || extractDomain(resolvedUrl || ''),
        description: metadata.description || `Visit ${extractDomain(resolvedUrl || '')}`,
        domain: metadata.domain || extractDomain(resolvedUrl || ''),
        favicon: metadata.favicon || `https://www.google.com/s2/favicons?domain=${extractDomain(resolvedUrl || '')}&sz=32`,
        thumbnail: metadata.thumbnail,
        author: metadata.author,
        video_id: metadata.video_id,
        duration: metadata.duration,
        view_count: metadata.view_count,
        redirect_info: metadata.redirect_info,
        realUrl: resolvedUrl, // Use the resolved URL we found
        url: resolvedUrl
      };

      // Cache individual result (if enabled)
      if (CACHE_ENABLED) {
        const individualCacheKey = getCacheKey(url, includeRedirectInfo);
        setCachedData(individualCacheKey, result);
      }

      originalIndices.forEach((originalIndex) => {
        finalResults[originalIndex] = result;
      });
    });

    // Cache the complete batch result (if enabled)
    if (CACHE_ENABLED) {
      const batchCacheKey = getBatchCacheKey(urls, includeRedirectInfo);
      setCachedData(batchCacheKey, finalResults);
    }

    return finalResults;

  } catch (error) {
    logger.warn('urlMetadata', 'Backend batch URL metadata failed, using fallbacks', { error: error.message });

    // Create fallback metadata for any uncached URLs
    const fallbackResults = [...cachedResults];

    uncachedUrls.forEach((url) => {
      const domain = extractDomain(url);
      const fallback = {
        title: domain,
        description: `Visit ${domain}`,
        favicon: `https://www.google.com/s2/favicons?domain=${domain}&sz=32`,
        domain: domain,
        url: url,
        realUrl: url
      };
      const originalIndices = uncachedIndicesByUrl.get(url) || [];
      originalIndices.forEach((originalIndex) => {
        fallbackResults[originalIndex] = fallback;
      });
    });

    return fallbackResults;
  }
};

// YouTube batch processing for video sources
export const fetchYouTubeVideosMetadata = async (sources) => {
  // Create cache key based on source URLs (if caching enabled)
  if (CACHE_ENABLED) {
    const sourceUrls = sources.map(source => source.url).sort();
    const youtubeCacheKey = `${CACHE_PREFIX}youtube_batch_${btoa(sourceUrls.join('|'))}`;

    // Check cache first
    const cachedYouTubeData = getCachedData(youtubeCacheKey);
    if (cachedYouTubeData) {
      return cachedYouTubeData;
    }
  }

  try {
    const apiBaseUrl = configApiUrl ?
      configApiUrl.replace(/\/$/, '') :
      '';
    const apiUrl = `${apiBaseUrl}/api/url/youtube/batch-metadata`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ sources }),
    });

    if (!response.ok) {
      throw new Error(`Backend API returned ${response.status}`);
    }

    const data = await response.json();
    const result = data.videos || [];

    // Cache the result (if enabled)
    if (CACHE_ENABLED) {
      const sourceUrls = sources.map(source => source.url).sort();
      const youtubeCacheKey = `${CACHE_PREFIX}youtube_batch_${btoa(sourceUrls.join('|'))}`;
      setCachedData(youtubeCacheKey, result);
    }

    return result;

  } catch (error) {
    logger.warn('urlMetadata', 'Backend YouTube batch metadata failed', { error: error.message });

    // Fallback: basic YouTube metadata for each source
    return sources.map(source => ({
      title: source.title || 'YouTube Video',
      description: 'Watch this video on YouTube',
      domain: 'youtube.com',
      favicon: 'https://www.youtube.com/favicon.ico',
      thumbnail: 'https://img.youtube.com/vi/default/mqdefault.jpg'
    }));
  }
};

export const extractDomain = (url) => {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname.replace('www.', '');
  } catch {
    return url;
  }
};

// Cache management utilities
export const getCacheStats = () => {
  try {
    let cacheEntries = 0;
    let totalSize = 0;
    let oldestEntry = Date.now();
    let newestEntry = 0;

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_PREFIX)) {
        cacheEntries++;
        const item = localStorage.getItem(key);
        if (item) {
          totalSize += item.length;
          try {
            const { timestamp } = JSON.parse(item);
            if (timestamp < oldestEntry) oldestEntry = timestamp;
            if (timestamp > newestEntry) newestEntry = timestamp;
          } catch (e) {
            // Skip malformed entries
          }
        }
      }
    }

    return {
      entries: cacheEntries,
      sizeKB: Math.round(totalSize / 1024),
      oldestEntry: cacheEntries > 0 ? new Date(oldestEntry).toISOString() : null,
      newestEntry: cacheEntries > 0 ? new Date(newestEntry).toISOString() : null,
      ttlHours: CACHE_TTL / (1000 * 60 * 60)
    };
  } catch (error) {
    logger.warn('urlMetadata', 'Error getting cache stats', { error: error.message });
    return { error: error.message };
  }
};

export const clearMetadataCache = () => {
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    // Debug: Cache cleanup completed
    // console.log(`Cleared ${keysToRemove.length} metadata cache entries`);
    return keysToRemove.length;
  } catch (error) {
    logger.warn('urlMetadata', 'Error clearing metadata cache', { error: error.message });
    return 0;
  }
};

// Enhanced metadata for known domains
export const getEnhancedMetadata = (url, children) => {
  const domain = extractDomain(url);
  const title = children || url;

  const enhancedMetadata = {
    'github.com': {
      title: title.includes('github.com') ? 'GitHub Repository' : title,
      description: 'Explore this repository on GitHub',
      icon: '📁'
    },
    'stackoverflow.com': {
      title: title.includes('stackoverflow.com') ? 'Stack Overflow Question' : title,
      description: 'Developer Q&A platform',
      icon: '❓'
    },
    'medium.com': {
      title: title.includes('medium.com') ? 'Medium Article' : title,
      description: 'Read this article on Medium',
      icon: '📝'
    },
    'youtube.com': {
      title: title.includes('youtube.com') ? 'YouTube Video' : title,
      description: 'Watch this video on YouTube',
      icon: '🎥'
    },
    'linkedin.com': {
      title: title.includes('linkedin.com') ? 'LinkedIn Profile/Post' : title,
      description: 'View on LinkedIn',
      icon: '💼'
    },
    'quantumzero.io': {
      title: title.includes('quantumzero.io') ? 'QuantumZero Official Website' : title,
      description: 'Primary source of information for QuantumZero\'s services, mission, and current offerings',
      icon: '🚀'
    },
    'accenture.com': {
      title: title.includes('accenture.com') ? 'Accenture Applied Intelligence' : title,
      description: 'Global consulting with dedicated AI/ML practice',
      icon: '🏢'
    }
  };

  return enhancedMetadata[domain] || {
    title: title,
    description: `Visit ${domain}`,
    icon: '🌐'
  };
};

// Helper function to check if URL is a direct YouTube URL
export const isYouTubeURL = (url) => {
  if (!url) return false;
  const youtubeRegex = /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/;
  return youtubeRegex.test(url);
};

// Enhanced function to check if URL might be a YouTube video (including redirects)
export const isPotentialYouTubeURL = (url, title) => {
  // First check if it's a direct YouTube URL
  if (isYouTubeURL(url)) return true;

  // Check if it's a Vertex AI redirect that might lead to YouTube
  if (url && url.includes('vertexaisearch.cloud.google.com')) {
    // Check if the title suggests it's from YouTube
    if (title) {
      const lowerTitle = title.toLowerCase();
      return lowerTitle.includes('youtube.com') || lowerTitle.includes('youtu.be');
    }
  }

  return false;
};
